import { Head, Link } from '@inertiajs/react';
import { ArrowLeft, Download, Paperclip } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import type { AdminEmailsShow, OutboundEmail } from '@/types';
import { DeleteEmailButton } from './components/DeleteEmailButton';
import { ResendEmailButton } from './components/ResendEmailButton';

type Attachment = NonNullable<OutboundEmail['attachments']>[number];

const base64Bytes = (content: string) => {
  const binary = atob(content);
  return Uint8Array.from(binary, (char) => char.charCodeAt(0));
};

const attachmentDownloadHref = (att: Attachment) => {
  const contentType = att.contentType || 'application/octet-stream';
  return att.encoding === 'base64'
    ? `data:${contentType};base64,${att.content}`
    : `data:${contentType};charset=utf-8,${encodeURIComponent(att.content)}`;
};

const attachmentSize = (att: Attachment) => {
  const bytes =
    att.encoding === 'base64'
      ? base64Bytes(att.content).byteLength
      : new TextEncoder().encode(att.content).byteLength;
  return bytes < 1024 ? `${bytes} B` : `${(bytes / 1024).toFixed(1)} KB`;
};

const attachmentRawText = (att: Attachment) =>
  att.encoding === 'base64'
    ? new TextDecoder().decode(base64Bytes(att.content))
    : att.content;

export default function Show({ email, title }: AdminEmailsShow) {
  const statusColors: Record<string, string> = {
    sent: 'bg-green-100 text-green-800 border-green-300',
    pending: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    failed: 'bg-red-100 text-red-800 border-red-300',
  };

  const statusLabels: Record<string, string> = {
    sent: 'Sent',
    pending: 'Pending',
    failed: 'Failed',
  };

  const attachments = email.attachments ?? [];

  return (
    <>
      <Head title={title} />
      <div className="container mx-auto space-y-6 py-8">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" asChild>
            <Link href="/admin/emails">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="font-bold text-3xl tracking-tight">Email Details</h1>
            <p className="text-muted-foreground">
              View email content and metadata
            </p>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-3">
          <div className="space-y-6 lg:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Email Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <div className="font-medium text-muted-foreground text-sm">
                    Subject
                  </div>
                  <div className="mt-1 text-sm">{email.subject}</div>
                </div>

                <div>
                  <div className="font-medium text-muted-foreground text-sm">
                    Status
                  </div>
                  <Badge
                    variant="outline"
                    className={`mt-1 ${statusColors[email.status]}`}
                  >
                    {statusLabels[email.status]}
                  </Badge>
                </div>

                <div>
                  <div className="font-medium text-muted-foreground text-sm">
                    Template
                  </div>
                  <div className="mt-1 font-mono text-sm">
                    {email.templateName}
                  </div>
                </div>

                <div>
                  <div className="font-medium text-muted-foreground text-sm">
                    Recipient
                  </div>
                  <div className="mt-1 text-sm">{email.to}</div>
                </div>

                {email.cc && email.cc.length > 0 && (
                  <div>
                    <div className="font-medium text-muted-foreground text-sm">
                      CC
                    </div>
                    <div className="mt-1 text-sm">{email.cc.join(', ')}</div>
                  </div>
                )}

                {email.bcc && email.bcc.length > 0 && (
                  <div>
                    <div className="font-medium text-muted-foreground text-sm">
                      BCC
                    </div>
                    <div className="mt-1 text-sm">{email.bcc.join(', ')}</div>
                  </div>
                )}

                {email.replyTo && (
                  <div>
                    <div className="font-medium text-muted-foreground text-sm">
                      Reply To
                    </div>
                    <div className="mt-1 text-sm">{email.replyTo}</div>
                  </div>
                )}

                <div>
                  <div className="font-medium text-muted-foreground text-sm">
                    Created At
                  </div>
                  <div className="mt-1 text-sm">
                    {new Date(email.createdAt).toLocaleString('en-US', {
                      dateStyle: 'long',
                      timeStyle: 'medium',
                    })}
                  </div>
                </div>

                {email.sentAt && (
                  <div>
                    <div className="font-medium text-muted-foreground text-sm">
                      Sent At
                    </div>
                    <div className="mt-1 text-sm">
                      {new Date(email.sentAt).toLocaleString('en-US', {
                        dateStyle: 'long',
                        timeStyle: 'medium',
                      })}
                    </div>
                  </div>
                )}

                {email.externalMessageId && (
                  <div>
                    <div className="font-medium text-muted-foreground text-sm">
                      External Message ID
                    </div>
                    <div className="mt-1 break-all font-mono text-xs">
                      {email.externalMessageId}
                    </div>
                  </div>
                )}

                {email.failureReason && (
                  <div>
                    <div className="font-medium text-muted-foreground text-sm">
                      Failure Reason
                    </div>
                    <div className="mt-1 text-destructive text-sm">
                      {email.failureReason}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Paperclip className="h-4 w-4" />
                  Attachments
                  <Badge variant="outline">{attachments.length}</Badge>
                </CardTitle>
                <CardDescription>
                  Files delivered alongside this email
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {attachments.length === 0 ? (
                  <p className="text-muted-foreground text-sm">No attachments</p>
                ) : (
                  attachments.map((att, i) => {
                    const isText =
                      att.contentType?.startsWith('text/') ||
                      att.filename.endsWith('.ics');
                    return (
                      <div
                        key={`${att.filename}-${i}`}
                        className="space-y-2 rounded-md border p-3"
                      >
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="truncate font-mono text-sm">
                              {att.filename}
                            </div>
                            <div className="text-muted-foreground text-xs">
                              {att.contentType || 'application/octet-stream'} ·{' '}
                              {attachmentSize(att)}
                            </div>
                          </div>
                          <Button variant="outline" size="sm" asChild>
                            <a
                              href={attachmentDownloadHref(att)}
                              download={att.filename}
                            >
                              <Download className="mr-1 h-3 w-3" />
                              Download
                            </a>
                          </Button>
                        </div>
                        {isText && (
                          <details>
                            <summary className="cursor-pointer text-muted-foreground text-xs">
                              View content
                            </summary>
                            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap rounded-md bg-muted p-3 font-mono text-xs">
                              {attachmentRawText(att)}
                            </pre>
                          </details>
                        )}
                      </div>
                    );
                  })
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Actions</CardTitle>
                <CardDescription>Manage this email</CardDescription>
              </CardHeader>
              <CardContent className="space-y-2">
                <ResendEmailButton emailId={email.id} />
                {email.status === 'pending' ? (
                  <DeleteEmailButton emailId={email.id} />
                ) : null}
              </CardContent>
            </Card>
          </div>

          <div className="space-y-6 lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Email Preview</CardTitle>
                <CardDescription>
                  HTML content as it appears to recipients
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="rounded-md border bg-muted/50 p-4">
                  <iframe
                    srcDoc={email.htmlContent}
                    className="h-[600px] w-full rounded-md bg-white"
                    title="Email Preview"
                    sandbox="allow-same-origin"
                  />
                </div>
              </CardContent>
            </Card>

            {email.textContent && (
              <Card>
                <CardHeader>
                  <CardTitle>Plain Text Version</CardTitle>
                  <CardDescription>
                    Fallback content for plain text email clients
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="whitespace-pre-wrap rounded-md bg-muted p-4 font-mono text-sm">
                    {email.textContent}
                  </pre>
                </CardContent>
              </Card>
            )}

            {email.templateData ? (
              <Card>
                <CardHeader>
                  <CardTitle>Template Data</CardTitle>
                  <CardDescription>
                    Data passed to the email template
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <pre className="whitespace-pre-wrap rounded-md bg-muted p-4 font-mono text-sm">
                    {JSON.stringify(email.templateData, null, 2)}
                  </pre>
                </CardContent>
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </>
  );
}
